import React from "react";
import classNames from "classnames";
import dayjs from "dayjs";

/**
 * Ant Design 5 Calendar + dayjs: fullCellRender로 날짜 숫자·공휴일 라벨을 그리고,
 * 하단 content는 renderDateContent로 채운다.
 *
 * @param {Object} options
 * @param {Record<string, string>} options.holidayMap - YYYYMMDD → 공휴일명
 * @param {(date: import('dayjs').Dayjs) => React.ReactNode} options.renderDateContent - type==='date'일 때만 호출
 * @param {(date: import('dayjs').Dayjs) => React.CSSProperties | undefined} [options.getDateContentStyle] - 날짜 content 영역 스타일
 * @returns {{ fullCellRender: Function }}
 */
export function createCalendarHolidayRenders({
	holidayMap,
	renderDateContent,
	getDateContentStyle,
}) {
	const fullCellRender = (date, info) => {
		const d = dayjs(date);
		const prefixCls = info.prefixCls;
		const calendarPrefixCls = `${prefixCls}-calendar`;

		if (info.type === "month") {
			const months = info.locale?.shortMonths || [
				"1월",
				"2월",
				"3월",
				"4월",
				"5월",
				"6월",
				"7월",
				"8월",
				"9월",
				"10월",
				"11월",
				"12월",
			];
			const monthIndex = d.month();
			const isTodayMonth = d.isSame(
				dayjs(info.today),
				"month",
			);
			return (
				<div
					className={classNames(
						`${prefixCls}-cell-inner`,
						`${calendarPrefixCls}-date`,
						{
							[`${calendarPrefixCls}-date-today`]:
								isTodayMonth,
						},
					)}>
					<div
						className={`${calendarPrefixCls}-date-value`}>
						{months[monthIndex]}
					</div>
					<div
						className={`${calendarPrefixCls}-date-content`}
					/>
				</div>
			);
		}

		const locKey = d.format("YYYYMMDD");
		const holidayName = holidayMap[locKey] || "";
		const isLordsDay = d.day() === 0;
		const isSaturday = d.day() === 6;
		const isRed = isLordsDay || !!holidayName;
		const isBlue = isSaturday;
		const isToday = d.isSame(dayjs(info.today), "day");

		return (
			<div
				className={classNames(
					`${prefixCls}-cell-inner`,
					`${calendarPrefixCls}-date`,
					{
						[`${calendarPrefixCls}-date-today`]:
							isToday,
					},
				)}>
				<div
					className={`${calendarPrefixCls}-date-value utils-calendar-date-value-row`}>
					<span
						className={
							isRed
								? "utils-calendar-date-number utils-calendar-date-number--red"
								: isBlue
									? "utils-calendar-date-number utils-calendar-date-number--blue"
									: "utils-calendar-date-number"
						}>
						{String(d.date()).padStart(
							2,
							"0",
						)}
					</span>
					{holidayName ? (
						<span
							className="utils-calendar-holiday-label"
							title={holidayName}>
							{holidayName}
						</span>
					) : null}
				</div>
				<div
					className={`${calendarPrefixCls}-date-content`}
					style={
						getDateContentStyle
							? getDateContentStyle(d)
							: undefined
					}>
					{renderDateContent
						? renderDateContent(d)
						: null}
				</div>
			</div>
		);
	};

	return { fullCellRender };
}
